/*
 * PersonalDictEditor — Full-Screen-Pane fuer das Personal-Dictionary
 * (Spec §10 Phase 4).
 *
 * Pure-Wrapper-Strategie analog MemoryEditor: open() instantiert einen
 * eigenen ProseEditor, close() raeumt ihn ab. Der Personal-Dict-Pane hat
 * keine Section-Hierarchie, kein Inline-Code, keine Spellcheck (jedes
 * Wort im Pane ist per Definition akzeptiert) und kein Note/Info-Feld.
 *
 * Save-Strategie (Boris hat sich fuer "Save on close" entschieden, nicht
 * Debounce): close() liest die aktuellen Paragraphen, kanonisiert sie zur
 * Wort-Liste und schickt sie EINMAL an `manager.setPersonal(words)` +
 * `PUT /api/user/spellcheck { personal: words }`. Wenn die Liste sich
 * gegenueber dem Stand beim open() nicht geaendert hat, sparen wir uns den
 * HTTP-Call.
 */

import { handleSessionLost } from '../auth/AuthService.js'
import {
    wordsToParagraphs, paragraphsToWords, validateWord, _internal
} from './PersonalDictRoundtrip.js'
import { PaneId } from '../config.js'
import { renderEmptyHint } from '../editor/emptyHint.js'
import {
    attachProseEditorToPane, detachProseEditorFromPane,
    syncProseEditorBounds, isProseEditorModelEmpty,
    applyCommonPaneKeyFilters, showPaneEditorToast
} from '../editor/paneEditorLifecycle.js'

const { PERSONAL_LIST_MAX } = _internal

export const PERSONAL_DICT_EMPTY_HINT =
    'Your personal dictionary is empty. Words you add through the spellcheck '
    + 'menu (Cmd+. on a marked word → "Add to personal dictionary") appear here. '
    + 'Edit lines to add or remove words. Each line is one word. '
    + 'Changes are saved when you close this pane.'

const TOAST_LIST_TOO_LARGE =
    `Personal dictionary cap reached (${PERSONAL_LIST_MAX} entries). Remove some entries first.`
const TOAST_SAVE_FAILED =
    'Could not save personal dictionary — please try again.'
const TOAST_NOT_AUTHENTICATED =
    'You are not signed in — personal dictionary changes were not saved.'

export class PersonalDictEditor {
    /**
     * @param {object} opts
     * @param {object} opts.layout - LayoutManager
     * @param {object} opts.spellCheckManager - SpellCheckManager (Quelle fuer
     *   die initiale Wort-Liste, Empfaenger des Save-Resultats)
     * @param {object} [opts.dialogManager]
     * @param {object} [opts.renderer]
     * @param {Function} [opts.fetch] - Override fuer Tests (default global fetch)
     * @param {Function} [opts.onClose]
     * @param {Function} [opts.onAcceptListChanged] - wird gerufen nach close(),
     *   wenn die Personal-Dict-Liste tatsaechlich geaendert wurde. Caller (main.js)
     *   nutzt das, um alle Editor-Paragraphen neu zu pruefen — `setPersonal`
     *   alleine entfernt nur Warnings fuer jetzt akzeptierte Worte, aber bei
     *   *entfernten* Eintraegen muss die Welle neu erzeugt werden.
     */
    constructor(opts = {}) {
        this.layout = opts.layout || null
        this.dialogManager = opts.dialogManager || null
        this._renderer = opts.renderer || null
        this._spellCheckManager = opts.spellCheckManager || null
        this._fetch = opts.fetch
            || (typeof fetch !== 'undefined' ? (...a) => fetch(...a) : null)
        this._onClose = typeof opts.onClose === 'function' ? opts.onClose : null
        this._onAcceptListChanged = typeof opts.onAcceptListChanged === 'function'
            ? opts.onAcceptListChanged : null

        this.proseEditor = opts.proseEditor || null
        this.isOpen = false
        this._previousActivePane = null
        // Snapshot der Wort-Liste beim open() — wenn beim close() unveraendert,
        // sparen wir den HTTP-Call.
        this._initialWords = []
    }

    isActive() { return this.isOpen }

    /**
     * Oeffnet den Editor: Pane auf FULLSCREEN, ProseEditor mit dem aktuellen
     * Personal-Dict aus dem Manager bestuecken.
     *
     * @returns {Promise<boolean>} true bei Erfolg
     */
    async open() {
        if (this.isOpen) return true
        if (!this.layout || !this.layout.panes[PaneId.PERSONAL_DICT]) {
            throw new Error('PersonalDictEditor.open() requires layout with PaneId.PERSONAL_DICT')
        }
        if (!this._spellCheckManager) {
            throw new Error('PersonalDictEditor.open() requires spellCheckManager')
        }

        // Aktuellen Stand vom Manager holen — der spiegelt den Server-Stand
        // (im Bootstrap synchron via GET /api/user/spellcheck gepullt).
        const currentWords = this._spellCheckManager.getPersonal()
        this._initialWords = [...currentWords]

        // Pane sichtbar machen + ProseEditor instantiieren via geteiltem Helper
        const attached = attachProseEditorToPane({
            layout: this.layout,
            paneId: PaneId.PERSONAL_DICT,
            renderer: this._renderer
        })
        this._previousActivePane = attached.previousActivePane
        this.proseEditor = attached.proseEditor
        // Spellcheck im Personal-Dict-Pane gibt es nicht — jedes Wort ist
        // per Definition akzeptiert. Kein Manager → keine Wellen, kein
        // _renderLineProportional-Enrichment.
        this.proseEditor.view.spellCheckManager = null
        // Block-Format-Makros (`* `, `> `, `# `, `1. `, ` ``` `, `---`) sind
        // in einer Wortliste sinnlos — Trigger im Input deaktivieren.
        this.proseEditor.input.disableBlockMacros = true

        // Wort-Liste in Paragraphen
        const paragraphs = wordsToParagraphs(currentWords)
        this.proseEditor.applyRemoteChanges(() => {
            this.proseEditor.model.setParagraphsRT(paragraphs)
        })

        this.isOpen = true
        return true
    }

    /**
     * Schliesst den Editor und persistiert die Aenderungen einmal final.
     *
     * @param {object} [opts]
     * @param {boolean} [opts.flush=true] - false ueberspringt den Save
     * @returns {Promise<boolean>} true = Pane wurde geschlossen.
     *   false = Pane bleibt offen, weil das Listen-Cap (PERSONAL_LIST_MAX)
     *   ueberschritten ist. Aufrufer (UserMenu, Pane-Control) muss den
     *   Schliesswunsch ggf. wiederholen, nachdem der User Eintraege entfernt
     *   hat. Toast benachrichtigt den User; ohne diesen Schutz wuerden die
     *   ueberzaehligen Eintraege beim Pane-Close verloren gehen
     *   (Spellcheck-Review 2026-05-10 Finding C2).
     */
    async close(opts = {}) {
        if (!this.isOpen) return true
        const flush = opts.flush !== false

        // Aktuellen Modell-Stand kanonisieren — dann mit dem Stand beim open()
        // vergleichen. Nur bei Aenderung Server + Manager updaten.
        let nextWords = []
        let saveError = null
        if (this.proseEditor) {
            const paras = this._modelParagraphs()
            nextWords = paragraphsToWords(paras)
        }

        // Cap-Check VOR dem Schliessen: Server lehnt > 1000 mit 400 ab — wenn
        // wir den Pane jetzt zumachen, sind die ueberzaehligen User-Eintraege
        // weg. Statt schliessen: Toast + Pane offen lassen. Der User kann
        // Eintraege loeschen und nochmal close() ausloesen.
        if (flush && nextWords.length > PERSONAL_LIST_MAX) {
            this._showToast(TOAST_LIST_TOO_LARGE)
            return false
        }

        let listChanged = false
        if (flush && this._wordsDiffer(this._initialWords, nextWords)) {
            try {
                await this._saveToServer(nextWords)
                this._spellCheckManager.setPersonal(nextWords)
                listChanged = true
            } catch (err) {
                saveError = err
                this._handleSaveError(err)
            }
        }

        // Pane verstecken + vorherigen Editor-Pane wiederherstellen via Helper
        detachProseEditorFromPane({
            layout: this.layout,
            paneId: PaneId.PERSONAL_DICT,
            previousActivePane: this._previousActivePane
        })
        this._previousActivePane = null

        this.proseEditor = null
        this.isOpen = false
        this._initialWords = []

        // Re-Check der Editor-Paragraphen, falls Eintraege entfernt wurden:
        // `setPersonal` purged nur Warnings fuer jetzt akzeptierte Worte,
        // entfernte Eintraege brauchen einen aktiven Re-Check, sonst sieht
        // der User die Welle erst beim naechsten Reload.
        if (listChanged && this._onAcceptListChanged) {
            try { this._onAcceptListChanged() } catch (err) {
                console.warn('PersonalDictEditor.onAcceptListChanged threw:', err.message)
            }
        }

        if (this._onClose) {
            try { this._onClose(saveError) } catch (err) {
                console.warn('PersonalDictEditor.onClose threw:', err.message)
            }
        }
        return true
    }

    // ─── Keydown-Filter ────────────────────────────────────────────────────

    /**
     * Filtert Format-Shortcuts und Block-Strukturierung VOR der Delegation
     * an ProseEditor — der Personal-Dict-Pane ist eine reine Plain-Text-
     * Liste, da macht Bold/Italic/Headings/Listen/Section-Lock/Suche keinen
     * Sinn.
     *
     * Returns true wenn der Wrapper den Event geschluckt hat (Caller darf
     * NICHT mehr an proseEditor.input.handleKeydown weiterreichen).
     *
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    handleKeydown(e) {
        if (!e) return false
        const cmdKey = e.metaKey || e.ctrlKey

        // Format-Shortcuts Cmd+B/I/U/E (Bold/Italic/Underline/Inline-Code) —
        // im Personal-Dict eine reine Wortliste, da macht Formatieren keinen
        // Sinn. Memory hat sie bewusst NICHT gefiltert (Bold/Italic darf in
        // Memory-Text). Backtick-Auto-Makro lass durch (kann als Buchstabe
        // in einem Wort denkbar sein).
        if (cmdKey && !e.altKey && !e.shiftKey) {
            const key = (e.key || '').toLowerCase()
            if (key === 'b' || key === 'i' || key === 'u' || key === 'e') {
                e.preventDefault()
                return true
            }
        }
        // Tab: List-Indent — keine Listen im Personal-Dict, schluck den Tab.
        if (e.key === 'Tab' && !cmdKey && !e.altKey) {
            e.preventDefault()
            return true
        }
        // Gemeinsame Pane-Filter (Separator, Suche, Cmd+Enter, Alt+Enter).
        return applyCommonPaneKeyFilters(e)
    }

    // ─── Render ────────────────────────────────────────────────────────────

    render(offsetX, offsetY, setCharFn) {
        if (!this.proseEditor) return
        this.proseEditor.render(offsetX, offsetY, setCharFn)

        if (this.isModelEmpty()) {
            this._renderEmptyHint(offsetX, offsetY, setCharFn)
        }
    }

    getPaneCursorPosition() {
        return this.proseEditor ? this.proseEditor.getPaneCursorPosition() : null
    }

    syncBounds() {
        syncProseEditorBounds({
            proseEditor: this.proseEditor,
            layout: this.layout,
            paneId: PaneId.PERSONAL_DICT
        })
    }

    isModelEmpty() {
        return isProseEditorModelEmpty(this.proseEditor)
    }

    /** @returns {string[]} Aktueller Modell-Stand, kanonisiert. */
    getCurrentWords() {
        if (!this.proseEditor) return []
        return paragraphsToWords(this._modelParagraphs())
    }

    // ─── Interne Helfer ────────────────────────────────────────────────────

    _modelParagraphs() {
        const model = this.proseEditor?.model
        if (!model || !Array.isArray(model.paragraphs)) return []
        return model.paragraphs.map(p => ({ text: p.text || '' }))
    }

    _wordsDiffer(a, b) {
        if (a.length !== b.length) return true
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return true
        }
        return false
    }

    async _saveToServer(words) {
        if (!this._fetch) return
        const res = await this._fetch('/api/user/spellcheck', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ personal: words })
        })
        if (res.status === 401) {
            handleSessionLost()
            const err = new Error('Nicht eingeloggt')
            err.code = 'not_authenticated'
            throw err
        }
        if (!res.ok) {
            const err = new Error(`Save failed: ${res.status}`)
            err.code = res.status === 400 ? 'invalid_input' : 'save_failed'
            throw err
        }
    }

    _handleSaveError(err) {
        if (err && err.code === 'not_authenticated') {
            this._showToast(TOAST_NOT_AUTHENTICATED)
        } else {
            console.warn('Personal-Dict save failed:', err && err.message)
            this._showToast(TOAST_SAVE_FAILED)
        }
    }

    _renderEmptyHint(offsetX, offsetY, setCharFn) {
        const view = this.proseEditor?.view
        const width = view?.width || 80
        renderEmptyHint(PERSONAL_DICT_EMPTY_HINT, offsetX, offsetY, setCharFn, width, this._renderer)
    }

    _showToast(message) {
        showPaneEditorToast(this.dialogManager, 'PersonalDictEditor', message)
    }
}

export const _testHelpers = {
    PERSONAL_DICT_EMPTY_HINT,
    TOAST_LIST_TOO_LARGE,
    TOAST_SAVE_FAILED,
    validateWord
}
